import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { globalHistory } from '@reach/router'
import { navigate } from 'gatsby'
import { debounce, isEqual } from 'lodash'

type FilterFieldValueType = 'number' | 'string' | 'boolean'

export type FilterFieldDefinition = {
  name: string
  label: string
  type: FilterFieldValueType,
  widget: string,
  nullValue?: number | string,
  hidden?: boolean
  dynamicChoicesFrom?: string,
  options?: {
    value: number | string,
    label: string,
    defaultValue?: boolean
  }[],
}

export type FilterDefinition = {
  search: boolean
  fields: FilterFieldDefinition[]
}

const EMPTY_DEFINITION: FilterDefinition = { search: false, fields: [] }

// Filter state lives in the query string so it survives navigation and can
// be shared: `q` carries the search, `f_<field>` each filter field.
const FIELD_PARAM_PREFIX = 'f_'

export const parseFilterSearch = (
  search: string
): { q: string, state: Record<string, unknown>, page: number } => {
  const params = new URLSearchParams(search)
  const state: Record<string, unknown> = {}
  let q = ''
  let page = 1
  params.forEach((value, key) => {
    if (key === 'q') {
      q = value
    } else if (key === 'p') {
      const parsed = parseInt(value)
      if (!isNaN(parsed) && parsed > 1) page = parsed
    } else if (key.startsWith(FIELD_PARAM_PREFIX)) {
      state[key.slice(FIELD_PARAM_PREFIX.length)] = value
    }
  })
  return { q, state, page }
}

export const buildFilterSearch = (
  filterState: Record<string, unknown>, page = 1
): string => {
  const params = new URLSearchParams()
  const { q, ...fields } = filterState
  if (q) {
    params.set('q', String(q))
  }
  if (page > 1) {
    params.set('p', String(page))
  }
  Object.keys(fields).sort().forEach(key => {
    const value = fields[key]
    if (value !== undefined && value !== null && value !== '') {
      params.set(`${FIELD_PARAM_PREFIX}${key}`, String(value))
    }
  })
  return params.toString()
}

// Values hydrated from the URL are strings; once the list registers its
// definition, coerce them to the types its fields expect.
const coerceState = (
  state: Record<string, unknown>, definition: FilterDefinition
): Record<string, unknown> => {
  const result: Record<string, unknown> = {}
  Object.entries(state).forEach(([key, value]) => {
    const field = definition.fields.find(f => f.name === key)
    if (!field || key === 'q') {
      result[key] = value
      return
    }
    if (field.type === 'number') {
      const parsed = parseInt(String(value))
      if (!isNaN(parsed)) result[key] = parsed
    } else if (field.type === 'boolean') {
      result[key] = value === 'true' || value === true
    } else {
      result[key] = value
    }
  })
  return result
}

const hydratedFrom = (search: string): {
  filterState: Record<string, unknown>
  searchQuery: string
  page: number
} => {
  const { q, state, page } = parseFilterSearch(search)
  return {
    filterState: q ? { ...state, q } : state,
    searchQuery: q,
    page,
  }
}

interface ProviderValue {
  filterChanged: (filter: Record<string, unknown>) => void
  filterValueChanged: (key: string, value: string) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  filterState: Record<string, unknown>
  filterDefinition: FilterDefinition
  isFilterSet: boolean
  isEnabled: boolean
  page: number
  setPage: (page: number) => void
  resetFilter: () => void
  setDefinition: (definition: FilterDefinition) => void
}

export const ListFilterContext = createContext<ProviderValue>({
  filterChanged: () => { /* noop */ },
  filterValueChanged: () => { /* noop */ },
  searchQuery: '',
  setSearchQuery: () => { /* noop */ },
  filterState: {},
  filterDefinition: { search: false, fields: [] },
  isFilterSet: false,
  isEnabled: false,
  page: 1,
  setPage: () => { /* noop */ },
  resetFilter: () => { /* noop */ },
  setDefinition: () => { /* noop */ },
})

type Props = {
  children?: React.ReactNode
}

export const ListFilterProvider: React.FC<Props> = ({ children }) => {
  // Read pathname from @reach/router's globalHistory singleton instead of
  // useLocation(), because this provider is mounted via wrapRootElement —
  // above Gatsby's <Location>/<Router>, where the LocationContext lives.
  const [pathname, setPathname] = useState(globalHistory.location.pathname)
  useEffect(() => globalHistory.listen(({ location }) => {
    setPathname(location.pathname)
  }), [])

  const [definition, setRawDefinition] = useState<FilterDefinition>(EMPTY_DEFINITION)
  const [initial] = useState(() => hydratedFrom(globalHistory.location.search || ''))
  const [filterState, setFilterState] = useState<Record<string, unknown>>(initial.filterState)
  const [searchQuery, setSearchQuery] = useState(initial.searchQuery)
  const [page, setPage] = useState(initial.page)
  const [resetPath, setResetPath] = useState(pathname)

  // Reset filter state when the route pathname changes, re-hydrating from
  // the destination's query string. Done during render (not in useEffect)
  // so the reset happens before any child component's useEffect sets a new
  // definition — otherwise the reset would race with and overwrite child
  // setters, hiding the filter UI on client-side nav.
  if (resetPath !== pathname) {
    const hydrated = hydratedFrom(globalHistory.location.search || '')
    setResetPath(pathname)
    setRawDefinition(EMPTY_DEFINITION)
    setFilterState(hydrated.filterState)
    setSearchQuery(hydrated.searchQuery)
    setPage(hydrated.page)
  }

  const setDefinition = useCallback((def: FilterDefinition) => {
    setRawDefinition(def)
    setFilterState(prev => coerceState(prev, def))
  }, [])

  // Mirror the filter state back into the URL. `replace` keeps filtering
  // from polluting history — back always leaves the page, never a filter.
  // Params the filter doesn't own (e.g. ?template= on the invoice form) are
  // preserved, not stripped.
  useEffect(() => {
    const params = new URLSearchParams(globalHistory.location.search || '')
    Array.from(params.keys()).forEach(key => {
      if (key === 'q' || key === 'p' || key.startsWith(FIELD_PARAM_PREFIX)) {
        params.delete(key)
      }
    })
    new URLSearchParams(buildFilterSearch(filterState, page)).forEach((value, key) => {
      params.set(key, value)
    })
    const search = params.toString()
    const current = (globalHistory.location.search || '').replace(/^\?/, '')
    if (globalHistory.location.pathname === pathname && search !== current) {
      void navigate(search ? `${pathname}?${search}` : pathname, { replace: true })
    }
  }, [filterState, page, pathname])

  const filterChanged = useCallback((filter: Record<string, unknown>) => {
    if (!isEqual(filter, filterState)) {
      setFilterState(filter)
      setPage(1)
    }
  }, [filterState])

  const resetFilter = useCallback(() => {
    setFilterState({})
    setSearchQuery('')
    setPage(1)
  }, [])

  const searchQueryChanged = useCallback((value: string) => {
    if (value === '' && filterState.q !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { q, ...rest } = filterState
      setFilterState(rest)
      setPage(1)
    } else if (value !== '' && filterState.q !== value) {
      setFilterState({ ...filterState, q: value })
      setPage(1)
    }
  }, [filterState, setFilterState])

  const filterValueChanged = useCallback((key: string, value: string) => {
    const fieldDefinition = definition.fields.find(field => field.name === key)
    if (!fieldDefinition) {
      return
    }
    let parsedValue = undefined as number | string | boolean | undefined
    const nullValue = fieldDefinition.nullValue
    if (fieldDefinition.type === 'number') {
      parsedValue = parseInt(value)
    } else if (fieldDefinition.type === 'boolean') {
      if (value !== 'true' && value !== 'false') {
        parsedValue = nullValue
      } else {
        parsedValue = value === 'true'
      }
    } else {
      parsedValue = value
    }
    if (parsedValue === nullValue && filterState[key] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _, ...rest } = filterState
      setFilterState(rest)
      setPage(1)
    } else if (parsedValue !== nullValue && filterState[key] !== parsedValue) {
      setFilterState({ ...filterState, [key]: parsedValue })
      setPage(1)
    }
  }, [filterState, definition.fields, setFilterState])

  useEffect(() => {
    debouncedSearch(searchQuery, searchQueryChanged)
  }, [searchQuery, searchQueryChanged])

  const value = useMemo(() => ({
    filterChanged,
    filterValueChanged,
    searchQuery,
    setSearchQuery,
    filterState,
    filterDefinition: definition,
    isFilterSet: Object.keys(filterState).length !== 0,
    isEnabled: definition.fields.length > 0 || definition.search,
    page,
    setPage,
    resetFilter,
    setDefinition,
  }), [
    filterChanged,
    filterState,
    filterValueChanged,
    searchQuery,
    setSearchQuery,
    definition,
    page,
    resetFilter,
    setDefinition,
  ])

  return (
    <ListFilterContext.Provider value={value}>
      {children}
    </ListFilterContext.Provider>
  )
}

const debouncedSearch = debounce((
  search: string,
  updateQuery: (q: string) => void,
) => {
  updateQuery(search)
}, 400)

export const useListFilter = (): ProviderValue => useContext(ListFilterContext)
